import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** A calm section: a small, quiet heading line and an optional «more» link – no rule, typography does the work. */
export function Section({ title, count, more, moreLabel = "Vis alle", intro, children, testId }) {
  return (
    <section className="min-w-0" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{title}{count ? <span className="ml-2 font-mono text-[12px] font-medium tabular-nums text-muted-foreground/70">{count}</span> : null}</h2>
        {more ? <Link to={more} className="group inline-flex items-center gap-1 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid={`${testId}-more`}>{moreLabel} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></Link> : null}
      </div>
      {intro ? <p className="mt-2 max-w-[680px] text-[15.5px] leading-[1.6] text-foreground/85" data-testid={`${testId}-intro`}>{intro}</p> : null}
      <div className={intro ? "mt-5" : "mt-3"}>{children}</div>
    </section>
  );
}

export function Quiet({ children, testId }) {
  return <p className="py-3 text-[13.5px] text-muted-foreground" data-testid={testId}>{children}</p>;
}

/** One row that opens something – title, a second quiet line and a right-aligned figure. */
export function RowLink({ to, title, sub, right, warn, testId }) {
  return (
    <Link to={to} className="group -mx-2 flex items-center gap-4 rounded-[10px] px-2 py-2.5 transition-colors hover:bg-black/[0.035]" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-medium text-foreground">{title}</div>
        {sub ? <div className={cn("mt-0.5 truncate text-[12.5px]", warn ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground")}>{sub}</div> : null}
      </div>
      {right ? <div className="shrink-0 text-right text-[12.5px] tabular-nums text-muted-foreground">{right}</div> : null}
      <ArrowRight className="h-3.5 w-3.5 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-[transform,opacity] group-hover:translate-x-0 group-hover:opacity-100" />
    </Link>
  );
}

/** Horizontal bars – quiet, mono numbers, used for «mer innsikt». */
export function Bars({ items, label, value, sub, testId }) {
  const max = Math.max(...items.map(value), 1);
  if (!items.length) return <Quiet>Ingenting å vise ennå.</Quiet>;
  return (
    <ul className="space-y-3" data-testid={testId}>
      {items.map((x, i) => (
        <li key={i} className="text-[13px]">
          <div className="flex items-baseline justify-between gap-3">{label(x)}<span className="shrink-0 font-mono text-[12px] tabular-nums text-muted-foreground">{sub(x)}</span></div>
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/[0.05]"><div className="grow-x h-full rounded-full bg-[hsl(var(--navy))]/70" style={{ width: `${Math.max(2, (value(x) / max) * 100)}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}
